import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useAppSelector } from '../../../hooks/hooksRedux';
import { RootState } from '../../../store';
import { defaultColors } from '../../../styles/style-guide';
import { useJurisdictions } from '../../../hooks/useJurisdictions';
import { FiRefreshCcw, FiCheckCircle, FiFileText, FiAlertCircle, FiPlus, FiX } from 'react-icons/fi';
import i18n from '../../../plugins/i18n/i18n';
import { useAuth } from '../../../store/auth';
import { BillingService } from '../../../services/BillingService';
import { AxiosInstance } from 'axios';
import { useApi } from '../../../services/hooks/apiRedux';
import { useTranslation } from 'react-i18next';
import { PageHeadline } from '../../../components/atoms';

type BillingStatus = 'paid' | 'pending' | 'overdue';

interface Company {
  id: number;
  name: string;
  incorporation_date: string;
}

interface Jurisdiction {
  id: number;
  description: string;
}

interface Config {
  jurisdiction: Jurisdiction;
  company_type: string;
  amount: number;
}

interface CompanyBilling {
  description: string;
  due_date: string;
  payment_date: string | null;
  total_amount: number;
  currency: string;
  type: string;
  status: BillingStatus;
  company: Company;
  config: Config;
  addons: any[];
}

interface PaginationData {
  current_page: number;
  data: CompanyBilling[];
  first_page_url: string;
  from: number;
  last_page: number;
  last_page_url: string;
  links: Array<{
    url: string | null;
    label: string;
    active: boolean;
  }>;
  next_page_url: string | null;
  path: string;
  per_page: number;
  prev_page_url: string | null;
  to: number;
  total: number;
}

interface UseBillingDataReturn {
  billings: CompanyBilling[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  pagination: PaginationData | null;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

const TABLE_HEADERS = [
  'others.othCompanyName',
  'labels.lblStatus',
  'labels.lblDueDate', 
  'labels.lblIncorporationDate', 
  'labels.lblAmount',
  'labels.lblDetails',
  'labels.lblActions'
] as const;

const STATUS_OPTIONS = [
  { value: 'paid', label: 'statuses.paid' },
  { value: 'pending', label: 'statuses.pending' },
  { value: 'overdue', label: 'statuses.overdue' }
] as const;

interface FilterOptions {
  status: string;
  jurisdiction: string;
  incorporationDate: string;
  exactDateFilter: boolean;
}

const useBillingData = (filters: FilterOptions): UseBillingDataReturn => {
  const { token } = useAuth();
  const api: AxiosInstance = useApi();
  const { t } = useTranslation();
  const { jurisdictions } = useJurisdictions();

  const [billings, setBillings] = useState<CompanyBilling[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationData | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const lastFetchKeyRef = useRef<string | null>(null);
  const isFetchingRef = useRef(false);

  const { status, jurisdiction, incorporationDate, exactDateFilter } = filters;

  const filtersKey = useMemo(
    () => JSON.stringify({ status, jurisdiction, incorporationDate, exactDateFilter }),
    [status, jurisdiction, incorporationDate, exactDateFilter]
  );

  const fetchBillings = useCallback(
    async (page: number, sortBy: string, sortDirection: string, force = false) => {
      if (!token) {
        setBillings([]);
        setPagination(null);
        return;
      }

      const key = `${token}|${i18n.language}|${page}|${filtersKey}|${sortBy}|${sortDirection}`;

      if (!force && lastFetchKeyRef.current === key) {
        return;
      }

      if (isFetchingRef.current) {
        return;
      }

      isFetchingRef.current = true;
      setLoading(true);
      setError(null);

      try {
        const queryParams: Record<string, any> = {};

        if (status) {
          queryParams.status = status;
        }

        if (jurisdiction) {
          const jurisdictionMatch = jurisdictions?.find(
            (j: any) => j.jurisdiction_description === jurisdiction
          );
          if (jurisdictionMatch) {
            queryParams.jurisdiction_id = jurisdictionMatch.jurisdiction_id;
          }
        }

        if (incorporationDate) {
          queryParams.incorporation_date = incorporationDate;
          queryParams.exact_date = exactDateFilter;
        }

        const billingResponse = await BillingService.getCompaniesPaymentStatus(
          api,
          {
            headers: {
              'Content-Language': i18n.language.toLowerCase(),
              Authorization: `Bearer ${token}`
            },
            params: {
              ...queryParams,
              page
            }
          },
          page
        );

        lastFetchKeyRef.current = key;
        setPagination(billingResponse);
        setBillings(billingResponse.data || []);
      } catch (err) {
        lastFetchKeyRef.current = null;
        const errorMessage = err instanceof Error ? err.message : t('errors.fetchBillingsFailed');
        setError(errorMessage);
        setBillings([]);
        setPagination(null);
      } finally {
        isFetchingRef.current = false;
        setLoading(false);
      }
    },
    [api, exactDateFilter, filtersKey, i18n.language, incorporationDate, jurisdiction, jurisdictions, status, t, token]
  );

  const handlePageChange = useCallback(
    (page: number) => {
      setCurrentPage(page);
      void fetchBillings(page, 'due_date', 'asc', true);
    },
    [fetchBillings]
  );

  const refetch = useCallback(() => {
    return fetchBillings(currentPage, 'due_date', 'asc', true);
  }, [fetchBillings, currentPage]);

  useEffect(() => {
    if (!token) {
      setBillings([]);
      setPagination(null);
      lastFetchKeyRef.current = null;
      return;
    }

    const initialKey = `${token}|${i18n.language}|1|${filtersKey}|due_date|asc`;
    if (lastFetchKeyRef.current === initialKey) {
      return;
    }

    setCurrentPage(1);
    void fetchBillings(1, 'due_date', 'asc', true);
  }, [fetchBillings, filtersKey, i18n.language, token]);

  // Remover a filtragem local já que agora é feita no backend
  const filteredBillings = useMemo(() => {
    return billings;
  }, [billings]);

  return {
    billings: filteredBillings,
    loading,
    error,
    refetch,
    pagination,
    currentPage,
    setCurrentPage: handlePageChange,
    hasNextPage: pagination ? !!pagination.next_page_url : false,
    hasPrevPage: pagination ? !!pagination.prev_page_url : false
  };
};

// Função utilitária para formatar datas 'YYYY-MM-DD' para 'YYYY-MM-DD' (identidade, mas mantém a estrutura para uso futuro)
function formatDateYMD(dateStr: string): string {
  if (!dateStr) return '';
  // Garante que só a parte da data seja exibida
  return dateStr.slice(0, 10);
}

interface PaginationProps {
  currentPage: number;
  lastPage: number;
  onPageChange: (page: number) => void;
  loading: boolean;
}

const Pagination: React.FC<PaginationProps> = ({ currentPage, lastPage, onPageChange, loading }) => {
  const { colors } = useAppSelector((state: RootState) => state.styleGuide.data);
  const primaryColor = colors.primary || defaultColors.primary;
  const { t } = useTranslation();

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (lastPage <= maxVisiblePages) {
      for (let i = 1; i <= lastPage; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 5; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(lastPage);
      } else if (currentPage >= lastPage - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = lastPage - 4; i <= lastPage; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(lastPage);
      }
    }
    
    return pages;
  };

  return (
    <div className="flex items-center justify-center gap-2 mt-6">
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1 || loading}
        className={`px-3 py-2 rounded-md text-sm font-medium ${
          currentPage === 1 || loading
            ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
            : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
        }`}
      >
        {t('labels.lblPevPage')}
      </button>

      {getPageNumbers().map((page, index) => (
        <button
          key={index}
          onClick={() => typeof page === 'number' && onPageChange(page)}
          disabled={loading || page === '...'}
          className={`px-3 py-2 rounded-md text-sm font-medium ${
            page === currentPage
              ? 'bg-blue-600 text-white'
              : page === '...'
              ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
              : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
          }`}
          style={page === currentPage ? { backgroundColor: primaryColor } : {}}
        >
          {page}
        </button>
      ))}

      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === lastPage || loading}
        className={`px-3 py-2 rounded-md text-sm font-medium ${
          currentPage === lastPage || loading
            ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
            : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
        }`}
      >
        {t('labels.lblNextPage')}
      </button>
    </div>
  );
};

const DashboardPaymentStatus: React.FC = React.memo(() => {
  const { jurisdictions } = useJurisdictions();
  const { t, i18n } = useTranslation();

  const [status, setStatus] = useState('');
  const [jurisdiction, setJurisdiction] = useState('');
  const [incorporationDate, setIncorporationDate] = useState('');
  const [exactDateFilter, setExactDateFilter] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedBilling, setSelectedBilling] = useState<CompanyBilling | null>(null);

  const filters = useMemo(() => ({
    status,
    jurisdiction,
    incorporationDate,
    exactDateFilter
  }), [status, jurisdiction, incorporationDate, exactDateFilter]);

  const { billings, loading, error, refetch, pagination, currentPage, setCurrentPage: handlePageChange } = useBillingData(filters);

  const tableHeaders = useMemo(() => 
    TABLE_HEADERS.map(header => t(header)), 
    [i18n.language]
  );

  const handleStatusChange = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    setStatus(event.target.value);
  }, []);

  const handleJurisdictionChange = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    setJurisdiction(event.target.value);
  }, []);

  const handleIncorporationDateChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    // Garante que só a parte da data seja usada
    setIncorporationDate(event.target.value.slice(0, 10));
  }, []);

  const handleExactDateFilterChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setExactDateFilter(event.target.checked);
  }, []);

  const handleClearFilters = useCallback(() => {
    setStatus('');
    setJurisdiction('');
    setIncorporationDate('');
    setExactDateFilter(false);
  }, []);

  const handleRefresh = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const handleMarkAsPaid = useCallback((billingId: number) => {
    console.log('Mark as paid:', billingId);
  }, []);

  const handleOpenDetailsModal = useCallback((billing: CompanyBilling) => {
    setSelectedBilling(billing);
    setShowDetailsModal(true);
  }, []);

  const handleCloseDetailsModal = useCallback(() => {
    setShowDetailsModal(false);
    setSelectedBilling(null);
  }, []);

  const renderError = () => (
    <div className="flex items-center justify-center py-8 text-red-600" role="alert">
      <FiAlertCircle className="mr-2" size={20} />
      <span>{error}</span>
      <button 
        onClick={handleRefresh}
        className="ml-4 px-3 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
        // aria-label={t('actions.retry')}
      >
        {t('actions.retry')}
      </button>
    </div>
  );

  const renderLoading = () => (
    <div className="text-center py-8" role="status" aria-live="polite">
      <div className="inline-flex items-center">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900 mr-3"></div>
        {t('labels.lblLoading')}...
      </div>
    </div>
  );

  const renderEmptyState = () => (
    <tr>
      <td colSpan={6} className="text-center py-8 text-gray-500">
        <div className="flex flex-col items-center">
          <FiFileText size={48} className="mb-4 text-gray-300" />
          <p>{t('labels.lblNoRecords')}</p>
        </div>
      </td>
    </tr>
  );

  const renderBillingRow = (billing: CompanyBilling, index: number) => (
    <tr
      key={`${billing.company.id}-${index}`}
      className="even:bg-gray-50 hover:bg-gray-100 transition-colors"
    >
      <td className="px-3 py-2 border">
        {billing.company.name ? billing.company.name : <i>{t('labels.lblNoCompanyName')}</i>}
      </td>
      <td className="px-3 py-2 border">
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${
          billing.status === 'paid' 
            ? 'bg-green-100 text-green-800'
            : billing.status === 'pending'
            ? 'bg-yellow-100 text-yellow-800'
            : 'bg-red-100 text-red-800'
        }`}>
          {t(`statuses.${billing.status}`)}
        </span>
      </td>
      <td className="px-3 py-2 border">
        {formatDateYMD(billing.due_date)}
      </td>
      <td className="px-3 py-2 border">
        {formatDateYMD(billing.company.incorporation_date)}
      </td>
      <td className="px-3 py-2 border font-medium">
        {billing.currency} {billing.total_amount.toFixed(2)}
      </td>
      <td className="px-3 py-2 border">
        <button
          onClick={() => handleOpenDetailsModal(billing)}
          className="inline-flex items-center px-3 py-1 text-sm font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 transition-colors"
          title={t('labels.lblViewDetails') as string}
          aria-label={t('labels.lblViewDetails') as string}
        >
          <FiPlus className="mr-1" size={14} />
          {t('labels.lblDetails')}
        </button>
      </td>
      <td className="px-3 py-2 border">
        <div className="flex items-center space-x-2">
          <button
            onClick={handleRefresh}
            className="text-blue-600 hover:text-blue-800 transition-colors p-1 rounded hover:bg-blue-50"
            title={t('actions.refresh') as string}
            aria-label={t('actions.refresh') as string}
          >
            <FiRefreshCcw size={16} />
          </button>
          <button
            onClick={() => handleMarkAsPaid(billing.company.id)}
            className="text-green-600 hover:text-green-800 transition-colors p-1 rounded hover:bg-green-50"
            title={t('actions.markAsPaid') as string}
            aria-label={t('actions.markAsPaid') as string}
            disabled={billing.status === 'paid'}
          >
            <FiCheckCircle size={16} />
          </button>
          {billing.payment_date && (
            <a
              href="#"
              className="text-purple-600 hover:text-purple-800 transition-colors p-1 rounded hover:bg-purple-50"
              title={t('actions.generateInvoice') as string}
              aria-label={t('actions.generateInvoice') as string}
            >
              <FiFileText size={16} />
            </a>
          )}
        </div>
      </td>
    </tr>
  );

  return (
    <div className="space-y-6">
      <div className="mb-6">
        <PageHeadline
          title={t('labels.lblPaymentStatus')}
          actions={
            <div className="flex space-x-2">
              <button
                onClick={handleClearFilters}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
              >
                {t('actions.clearFilters')}
              </button>
              <button
                onClick={handleRefresh}
                disabled={loading}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <FiRefreshCcw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                {t('actions.refresh')}
              </button>
            </div>
          }
        />
      </div>

      <section className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        <div className="space-y-1">
          <label htmlFor="jurisdiction-select" className="block text-sm font-medium text-gray-700">
            {t('companyTransfer.othSelectJurisdiction')}
          </label>
          <select
            id="jurisdiction-select"
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            value={jurisdiction}
            onChange={handleJurisdictionChange}
            aria-describedby="jurisdiction-help"
          >
            <option value="">{t('companyTransfer.othSelectJurisdiction')}</option>
            {jurisdictions?.map((juri) => (
              <option
                value={juri.jurisdiction_description}
                key={juri.jurisdiction_id}
              >
                {juri.jurisdiction_description}
              </option>
            ))}
          </select>

        </div>

        <div className="space-y-1">
          <label htmlFor="date-input" className="block text-sm font-medium text-gray-700">
            {t('labels.lblIncorporationDate')} {exactDateFilter ? `(${t('labels.lblExactDate')})` : `(${t('labels.lblStartingFrom')})`}
          </label>
          <input
            id="date-input"
            type="date"
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            value={incorporationDate}
            onChange={handleIncorporationDateChange}
            aria-describedby="date-help"
          />
          <div className="flex items-center space-x-2 mt-2">
            <input
              id="exact-date-checkbox"
              type="checkbox"
              checked={exactDateFilter}
              onChange={handleExactDateFilterChange}
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
            />
            <label htmlFor="exact-date-checkbox" className="text-sm text-gray-700">
              {t('labels.lblExactDateFilter')}
            </label>
          </div>

        </div>

        <div className="space-y-1">
          <label htmlFor="status-select" className="block text-sm font-medium text-gray-700">
            {t('labels.lblSelectStatus')}
          </label>
          <select
            id="status-select"
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            value={status}
            onChange={handleStatusChange}
            aria-describedby="status-help"
          >
            <option value="">{t('labels.lblSelectStatus')}</option>
            {STATUS_OPTIONS.map(({ value, label }) => (
              <option key={value} value={value}>
                {t(label)}
              </option>
            ))}
          </select>

        </div>
      </section>

      {/* Filtros Ativos */}
      {(status || jurisdiction || incorporationDate) && (
        <section className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-blue-900">
              {t('messages.filterActive')}:
            </h3>
            <button
              onClick={handleClearFilters}
              className="text-xs text-blue-600 hover:text-blue-800 underline"
            >
              {t('actions.clearFilters')}
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {status && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {t('labels.lblStatus')}: {t(`statuses.${status}`)}
                <button
                  onClick={() => setStatus('')}
                  className="ml-1 text-blue-600 hover:text-blue-800"
                  aria-label={t('actions.removeFilter') as string}
                >
                  <FiX size={12} />
                </button>
              </span>
            )}
            {jurisdiction && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {t('companyTransfer.othSelectJurisdiction')}: {jurisdiction}
                <button
                  onClick={() => setJurisdiction('')}
                  className="ml-1 text-blue-600 hover:text-blue-800"
                  aria-label={t('actions.removeFilter') as string}
                >
                  <FiX size={12} />
                </button>
              </span>
            )}
            {incorporationDate && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {t('labels.lblIncorporationDate')}: {incorporationDate} {exactDateFilter ? `(${t('labels.lblExactDate')})` : `(${t('labels.lblStartingFrom')})`}
                <button
                  onClick={() => setIncorporationDate('')}
                  className="ml-1 text-blue-600 hover:text-blue-800"
                  aria-label={t('actions.removeFilter') as string}
                >
                  <FiX size={12} />
                </button>
              </span>
            )}
          </div>
        </section>
      )}

      {/* Tabela de dados */}
      <section className="overflow-x-auto" role="region" aria-labelledby="payment-status-title">
        {error ? (
          renderError()
        ) : loading ? (
          renderLoading()
        ) : (
          <table className="min-w-full border-collapse text-sm" role="table">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                {tableHeaders.map((header) => (
                  <th
                    key={header}
                    className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border"
                  >
                    {t(header)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody role="rowgroup">
              {billings.length > 0 ? billings.map((billing, index) => renderBillingRow(billing, index)) : renderEmptyState()}
            </tbody>
          </table>
        )}
      </section>

      {/* Informações de paginação */}
      {pagination && (
        <div className="flex justify-between items-center mb-4 text-sm text-gray-600">
          <div>
            {t('messages.showingRange', {
              from: pagination.from,
              to: pagination.to,
              total: pagination.total
            })}
          </div>
          <div>
            {t('messages.pageInfo', {
              current: pagination.current_page,
              last: pagination.last_page
            })}
          </div>
        </div>
      )}

      {/* Paginação */}
      {pagination && (
        <Pagination
          currentPage={currentPage}
          lastPage={pagination.last_page}
          onPageChange={handlePageChange}
          loading={loading}
        />
      )}

      {/* Modal de Detalhes */}
      {showDetailsModal && selectedBilling && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            {/* Header da Modal */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                {t('labels.lblDetails')} - {selectedBilling.company.name}
              </h3>
              <button
                onClick={handleCloseDetailsModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                aria-label={t('actions.close') as string}
              >
                <FiX size={24} />
              </button>
            </div>

            {/* Conteúdo da Modal */}
            <div className="p-6">
              {selectedBilling.addons && selectedBilling.addons.length > 0 ? (
                <div className="space-y-4">
                  <h4 className="text-md font-medium text-gray-900 mb-4">
                    {t('labels.lblAddons')} ({selectedBilling.addons.length})
                  </h4>
                  <div className="grid gap-4">
                    {selectedBilling.addons.map((addon, index) => (
                      <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h5 className="font-medium text-gray-900 mb-1">
                              {addon.name || addon.description || t('labels.lblAddon')} #{index + 1}
                            </h5>
                            {addon.description && (
                              <p className="text-sm text-gray-600 mb-2">
                                {addon.description}
                              </p>
                            )}
                            {addon.amount && (
                              <p className="text-sm font-medium text-green-600">
                                {selectedBilling.currency} {addon.amount.toFixed(2)}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <FiFileText size={48} className="mx-auto mb-4 text-gray-300" />
                  <h4 className="text-lg font-medium text-gray-900 mb-2">
                    {t('labels.lblNoAddons')}
                  </h4>
                  <p className="text-gray-500">
                    {t('messages.noAddonsAvailable')}
                  </p>
                </div>
              )}
            </div>

            {/* Footer da Modal */}
            <div className="flex justify-end p-6 border-t border-gray-200">
              <button
                onClick={handleCloseDetailsModal}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
              >
                {t('actions.close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

DashboardPaymentStatus.displayName = 'DashboardPaymentStatus';

export { DashboardPaymentStatus };
